// Drag and drop, selecting a part, rotate and delete
import { Physics } from '../core/physics';
import { SFX } from '../core/audio';
import { cv, $ } from './dom';
import { G, usedOf, toWorld, toScreen } from './state';
import { setI } from './icons';
import type { Vec } from '../core/types';
import { hideHand, updateHud } from './hud';
import { rebuild, burst } from './play';

// ---------- Drag and drop ----------
// The part under a press: the part's own hitTest (e.g. the banana's arc), else its rectangles plus a margin
function partAt(w: Vec) {
  for (let i = G.pl.length - 1; i >= 0; i--) {
    const pl = G.pl[i], pd = Physics.partDef(pl.t);
    if (pd && pd.hitTest) { if (pd.hitTest(pl, w)) return i; continue; }
    for (const r of Physics.worldRects(pl)) {
      if (r.circle) { if (Math.hypot(w.x - r.x, w.y - r.y) < r.w / 2 + 16) return i; continue; }
      const a = -r.a, dx = w.x - r.x, dy = w.y - r.y;
      const lx = dx * Math.cos(a) - dy * Math.sin(a), ly = dx * Math.sin(a) + dy * Math.cos(a);
      if (Math.abs(lx) < r.w / 2 + 14 && Math.abs(ly) < r.h / 2 + 20) return i;
    }
  }
  return -1;
}

export function toolDown(e: PointerEvent, t: string) {
  const tile = e.currentTarget as HTMLElement;
  if (G.state !== 'build') return;
  e.preventDefault();
  if (G.level.tools[t] - usedOf(t) <= 0) { SFX.play('nope'); tile.classList.remove('shake'); void tile.offsetWidth; tile.classList.add('shake'); return; }
  SFX.play('pick');
  const w = toWorld(e.clientX, e.clientY);
  const pdn = Physics.partDef(t);
  const a = pdn && pdn.defaultA != null ? pdn.defaultA : 0;
  G.pl.push({ t, x: w.x, y: w.y, a });
  G.sel = -1;
  G.drag = { idx: G.pl.length - 1, off: { x: 0, y: 0 }, isNew: true, sx: e.clientX, sy: e.clientY, moved: true, from: null };
  hideHand(); updateHud(); updatePartMenu();
}

cv.addEventListener('pointerdown', e => {
  if (G.state !== 'build' || $('#game').hidden) return;
  const w = toWorld(e.clientX, e.clientY);
  const i = partAt(w);
  if (i < 0) { if (G.sel >= 0) { G.sel = -1; updatePartMenu(); } return; }
  const pl = G.pl[i];
  G.drag = { idx: i, off: { x: w.x - pl.x, y: w.y - pl.y }, isNew: false, sx: e.clientX, sy: e.clientY, moved: false, from: { x: pl.x, y: pl.y } };
});
addEventListener('pointermove', e => {
  const d = G.drag; if (!d) return;
  if (!d.moved && Math.hypot(e.clientX - d.sx, e.clientY - d.sy) > 8) { d.moved = true; if (G.sel !== d.idx) { G.sel = -1; } updatePartMenu(); }
  if (!d.moved) return;
  const w = toWorld(e.clientX, e.clientY);
  const pl = G.pl[d.idx];
  pl.x = w.x - d.off.x; pl.y = w.y - d.off.y - (d.isNew ? 30 : 0);
  d.overBox = e.clientX > innerWidth - parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--tool'));
});
addEventListener('pointerup', e => {
  const d = G.drag; if (!d) return;
  G.drag = null;
  const pl = G.pl[d.idx];
  if (!d.moved) { G.sel = G.sel === d.idx ? -1 : d.idx; SFX.play('click'); updatePartMenu(); return; }
  if (d.overBox) { G.pl.splice(d.idx, 1); G.sel = -1; SFX.play('remove'); rebuild(); updateHud(); updatePartMenu(); return; }
  const snapped = Physics.snap(G.level, G.pl, d.idx);
  if (!Physics.valid(G.level, snapped)) {
    SFX.play('nope');
    if (d.isNew) G.pl.splice(d.idx, 1); else { pl.x = d.from!.x; pl.y = d.from!.y; }
    G.sel = -1;
  } else {
    G.pl[d.idx] = { t: pl.t, x: snapped.x, y: snapped.y, a: pl.a };
    SFX.play(snapped.snapped ? 'snap' : 'place');
    if (snapped.snapped) burst(snapped.x, snapped.y, '#fff', 8, 2);
    G.sel = d.idx;
  }
  rebuild(); updateHud(); updatePartMenu();
});
addEventListener('pointercancel', () => { if (G.drag) { const d = G.drag; G.drag = null; if (d.isNew) G.pl.splice(d.idx, 1); rebuild(); updateHud(); } });

export function updatePartMenu() {
  const m = $('#partMenu');
  if (G.sel < 0 || G.state !== 'build' || !G.pl[G.sel] || $('#game').hidden) { m.hidden = true; return; }
  const pl = G.pl[G.sel], pd = Physics.partDef(pl.t)!, rot = pd.rot;
  let top = Infinity;
  for (const r of Physics.worldRects(pl)) top = Math.min(top, r.y - Math.max(r.w, r.h) / 2 * Math.abs(Math.sin(r.a)) - r.h / 2 - (r.circle ? r.w / 2 : 0));
  if (pd.menuTop) top = pd.menuTop(pl);
  const p = toScreen(pl.x, top);
  m.hidden = false;
  $('#rotL').hidden = $('#rotR').hidden = !rot;
  m.style.left = Math.max(90, Math.min(innerWidth - 220, p.x)) + 'px';
  m.style.top = Math.max(150, p.y - 14) + 'px';
}
function rotSel(dir: number) {
  const pl = G.pl[G.sel]; if (!pl) return;
  const pd = Physics.partDef(pl.t)!, rot = pd.rot;
  const old = pl.a;
  const a = pl.a as number;
  if (rot === 'tilt') pl.a = a > 0 ? -20 : 20;
  else if (rot === 'flip') pl.a = a ? 0 : 180;
  else if (typeof rot === 'number') pl.a = Math.max(pd.minA ?? -90, Math.min(pd.maxA ?? 90, a + dir * rot));
  else pl.a = Math.max(-90, Math.min(90, a + dir * 15));
  if (!Physics.valid(G.level, pl)) { pl.a = old; SFX.play('nope'); return; }
  SFX.play('rotate'); rebuild(); updatePartMenu();
}
$('#rotL').addEventListener('click', () => rotSel(-1));
$('#rotR').addEventListener('click', () => rotSel(1));
$('#delBtn').addEventListener('click', () => { if (G.sel < 0) return; G.pl.splice(G.sel, 1); G.sel = -1; SFX.play('remove'); rebuild(); updateHud(); updatePartMenu(); });
setI($('#rotL'), 'rotL'); setI($('#rotR'), 'rotR'); setI($('#delBtn'), 'trash');
